import { Component, OnInit, inject, DestroyRef, ElementRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, timeout } from 'rxjs/operators';

import { DrawerModule } from 'primeng/drawer';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { PaginatorModule } from 'primeng/paginator';
import { MenuModule } from 'primeng/menu';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';

import { ConfirmationService, MessageService, MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ClienteService, ContratoService, API_TO_SECTOR, SECTOR_TO_API } from '@core/services/domain-services';
import { compressImageIfNeeded } from '@shared/utils/compress-image.util';
import { NifService } from '@core/services/nif.service';

import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { Cliente } from '@core/models/models';

import { ClientesEstatisticas } from '@shared/api/services/estatisticas/estatisticas-info.service';

export interface ClienteVM extends Cliente {
  clienteAtivo: boolean;
  bi?: string;
}

@Component({
  selector: 'app-clientes',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DrawerModule,
    InputTextModule,
    SelectModule,
    SelectButtonModule,
    PaginatorModule,
    MenuModule,
    SkeletonModule,
    TooltipModule,
    EmptyStateComponent,
  ],
  templateUrl: './clientes.component.html',
  styleUrl: './clientes.component.scss',
})
export class ClientesComponent implements OnInit {
  @ViewChild('fileInput')
  fileInputRef!: ElementRef<HTMLInputElement>;

  @ViewChild('rowMenu')
  rowMenuRef!: Menu;

  private readonly fb = inject(FormBuilder);
  private readonly svc = inject(ClienteService);
  private readonly contratoSvc = inject(ContratoService);
  private readonly nifSvc = inject(NifService);
  private readonly msg = inject(MessageService);
  private readonly confirm = inject(ConfirmationService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  private readonly estatisticasClientesInfo = inject(ClientesEstatisticas);

  // ESTATÍSTICAS DA API
  totalClientes = 0;
  ativos = 0;
  inativos = 0;
  sectores = 0;

  // DADOS
  clientes: ClienteVM[] = [];

  loading = true;
  totalRecords = 0;

  showDialog = false;
  editing: ClienteVM | null = null;

  form!: FormGroup;

  get f(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  logoPreview: string | null = null;
  logoFile: File | null = null;

  nifSearching = false;

  private searchSubject = new Subject<string>();

  searchInput = '';
  searchTerm = '';

  filtrosForm!: FormGroup;
  showFilterSidebar = false;

  menuItems: MenuItem[] = [];

  currentPage = 1;
  pageSize = 15;

  readonly estadoOptions = [
    { label: 'Todos', value: null },
    { label: 'Activo', value: true },
    { label: 'Inactivo', value: false },
  ];

  readonly sectors = [
    { label: 'Banca e Finanças', value: 'BANCA_E_FINANCAS', enumValue: 1 },
    { label: 'Petróleo e Gás', value: 'PETROLEO_E_GAS', enumValue: 2 },
    { label: 'Aviação e Aeroportos', value: 'AVIACAO_E_AEROPORTOS', enumValue: 3 },
    { label: 'Telecomunicações', value: 'TELECOMUNICACOES', enumValue: 4 },
    { label: 'Saúde', value: 'SAUDE', enumValue: 5 },
    { label: 'Comércio e Retalho', value: 'COMERCIO_E_RETALHO', enumValue: 6 },
    { label: 'Hotelaria e Turismo', value: 'HOTELARIA_E_TURISMO', enumValue: 7 },
    { label: 'Indústria e Manufatura', value: 'INDUSTRIA_E_MANUFATURA', enumValue: 8 },
    { label: 'Sector Público', value: 'SECTOR_PUBLICO', enumValue: 9 },
    { label: 'Construção', value: 'CONSTRUCAO', enumValue: 10 },
    { label: 'Outro', value: 'OUTRO', enumValue: 99 },
  ];

  // FILTROS ACTIVOS
  get activeFilterCount(): number {
    if (!this.filtrosForm) {
      return 0;
    }

    const val = this.filtrosForm.value;

    return Object.keys(val).filter((key) => {
      const value = val[key];

      return (
        value !== null &&
        value !== undefined &&
        value !== ''
      );
    }).length;
  }

  ngOnInit(): void {
    // Adicionadas as validações obrigatórias exigidas pela API
    this.form = this.fb.group({
      nome: [
        '',
        [Validators.required, Validators.minLength(2)],
      ],
      nif: [
        '',
        [Validators.required, Validators.minLength(8), Validators.maxLength(20), Validators.pattern(/^[A-Za-z0-9]+$/)],
      ],
      email: ['', [Validators.required, Validators.email]],
      sector: [null],
      numTelefone: ['', [Validators.required, Validators.pattern(/^\+?[\d\s\-()]{9,20}$/)]],
      nomeResponsavel: ['', [Validators.required]],
      endereco: ['', [Validators.required]],
      website: [
        '',
        [Validators.required, Validators.pattern(/^(https?:\/\/)?(www\.)?[a-zA-Z0-9\-.]+\.[a-zA-Z]{2,}(\/\S*)?$/)],
      ],
      bi: [''],
    });

    this.filtrosForm = this.fb.group({
      Nome: [''],
      Nif: [''],
      NumTelefone: [''],
      NomeResponsavel: [''],
      Email: [''],
      Endereco: [''],
      WebSite: [''],
      SetorAtividadeEmpresaEnum: [null],
      IsActive: [null],
    });

    // Pesquisa
    this.searchSubject
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((term) => {
        this.filtrosForm.patchValue({
          Nome: term,
        });

        this.currentPage = 1;

        this.load();
      });

    this.load();

    this.estatisticasClientes();
  }

  // ESTATÍSTICAS, GET /api/Dashboard/ClientesEstatisticas
  private estatisticasClientes(): void {
    this.estatisticasClientesInfo
      .obterClienteEstatisticas()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (estatisticas) => {
          this.totalClientes = estatisticas.totalClientes;
          this.ativos = estatisticas.ativos;
          this.inativos = estatisticas.inativos;
          this.sectores = estatisticas.sectores;
          this.cdr.markForCheck();
        },

        error: (err) => {
          console.error(
            'Erro ao carregar estatísticas dos clientes:',
            err,
          );

          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail:
              'Não foi possível carregar as estatísticas dos clientes.',
            life: 5000,
          });
        },
      });
  }

  // CARREGAR CLIENTES
  load(queryParams: Record<string, any> = {}): void {
    this.loading = true;

    const params: Record<string, any> = {
      pageNumber: this.currentPage,
      pageSize: this.pageSize,

      ...this.filtrosForm.value,

      ...queryParams,
    };

    // Remover parâmetros vazios
    Object.keys(params).forEach((key) => {
      const value = params[key];

      if (
        value === null ||
        value === undefined ||
        value === ''
      ) {
        delete params[key];
      }
    });

    this.svc
      .getAll(params)
      .pipe(
        timeout(8000),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          const raw = res.data ?? [];

          this.clientes = raw.map(
            (c: any) =>
              this.mapApiCliente(c),
          );

          this.totalRecords =
            res.totalRecords ??
            this.clientes.length;

          this.carregarContagensContratos();

          this.loading = false;

          this.cdr.markForCheck();
        },

        error: (err) => {
          console.error(
            'Erro ao carregar clientes:',
            err,
          );

          this.clientes = [];
          this.totalRecords = 0;
          this.loading = false;

          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail:
              'Não foi possível carregar os clientes.',
            life: 5000,
          });

          this.cdr.markForCheck();
        },
      });
  }

  // MAPEAR CLIENTE DA API
  private mapApiCliente(c: any): ClienteVM {
    const isClienteAtivo = c.isActive !== undefined ? c.isActive : (c.IsActive !== undefined ? c.IsActive : (c.ativo !== undefined ? c.ativo : true));

    return {
      id: c.id,
      nome: c.nome,
      nif: c.nif,
      bi: c.bi,
      numTelefone: c.numTelefone,
      nomeResponsavel: c.nomeResponsavel,
      email: c.email,
      endereco: c.endereco,
      website: c.webSite,
      logoUrl: c.url,

      clienteAtivo: !!isClienteAtivo,
      isActive: !!isClienteAtivo,
      ativo: !!isClienteAtivo,

      sector:
        API_TO_SECTOR[
          c.setorAtividadeEmpresaEnum
        ] ?? undefined,

      totalContratosActivos:
        c.totalContratosActivos ??
        c.totalContratos ??
        c.numeroContratos ??
        c.quantidadeContratos ??
        c.contratos?.length ??
        0,
    };
  }

  private carregarContagensContratos(): void {
    const clientesComId = this.clientes.filter(
      (cliente): cliente is ClienteVM & { id: number } => cliente.id !== undefined,
    );

    if (!clientesComId.length) return;

    forkJoin(
      clientesComId.map((cliente) =>
        this.contratoSvc.getByCliente(cliente.id).pipe(
          map((res: any) => {
            const contratos = res?.data ?? (Array.isArray(res) ? res : []);
            return Array.isArray(contratos) ? contratos.length : 0;
          }),
          catchError(() => of<number | null>(null)),
        ),
      ),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((contagens) => {
        const contagensPorCliente = new Map(
          clientesComId.map((cliente, index) => [cliente.id, contagens[index]]),
        );

        this.clientes = this.clientes.map((cliente) => {
          if (cliente.id === undefined) return cliente;
          const total = contagensPorCliente.get(cliente.id);
          return total === null || total === undefined
            ? cliente
            : { ...cliente, totalContratosActivos: total };
        });
        this.cdr.markForCheck();
      });
  }

  onSearch(value: string): void {
    this.searchSubject.next(value);
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.showFilterSidebar = false;
    this.load();
  }

  clearFilters(): void {
    this.filtrosForm.reset();
  }

  openRowMenu(event: Event, c: ClienteVM, menu: Menu): void {
    const isAtivo = c.clienteAtivo;

    this.menuItems = [
      {
        label: 'Ver Perfil',
        icon: 'ri-eye-line',
        command: () => this.verDetalhe(c),
      },
      {
        label: 'Editar',
        icon: 'ri-pencil-line',
        command: () => this.openEdit(c),
      },
      {
        label: isAtivo ? 'Desativar' : 'Ativar',
        icon: isAtivo ? 'ri-close-circle-line' : 'ri-checkbox-circle-line',
        styleClass: isAtivo ? 'danger-item' : 'primary-item',
        command: () => this.toggleStatus(c),
      },
    ];

    menu.toggle(event);
  }

  toggleStatus(c: ClienteVM): void {
    if (!c.id) return;

    const isAtivo = c.clienteAtivo;
    const acao = isAtivo ? 'desativar' : 'ativar';

    this.confirm.confirm({
      header: `${isAtivo ? 'Desativar' : 'Ativar'} Cliente`,
      message: `Tem certeza que deseja ${acao} o cliente "${c.nome}"?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: isAtivo ? 'Desativar' : 'Ativar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: isAtivo ? 'p-button-danger' : 'p-button-primary',
      accept: () => {
        this.svc
          .toggleStatus(c.id!)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.msg.add({
                severity: 'success',
                summary: 'Sucesso',
                detail: `Cliente ${isAtivo ? 'desativado' : 'ativado'} com sucesso.`,
                life: 3000,
              });
              this.load();
              this.estatisticasClientes();
            },
            error: (err) => {
              console.error(`Erro ao ${acao} cliente:`, err);
              this.msg.add({
                severity: 'error',
                summary: 'Erro',
                detail: `Não foi possível ${acao} o cliente.`,
                life: 5000,
              });
            },
          });
      },
    });
  }

  onPageChange(event: any): void {
    const pageIndex = event.first / event.rows + 1;

    this.currentPage = pageIndex;

    this.pageSize = event.rows;

    this.load();
  }

  novoCliente(): void {
    this.router.navigate(['/admin/clientes/novo']);
  }

  verDetalhe(c: ClienteVM): void {
    if (c.id) {
      this.router.navigate(['/admin/clientes', c.id]);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // EDITAR
  // ─────────────────────────────────────────────────────────────

  openEdit(c: ClienteVM): void {
    this.editing = c;

    this.logoPreview = c.logoUrl ?? null;
    this.logoFile = null;

    this.form.patchValue({
      nome: c.nome ?? '',
      nif: c.nif ?? '',
      email: c.email ?? '',
      sector: c.sector ?? null,
      numTelefone: c.numTelefone ?? '',
      nomeResponsavel: c.nomeResponsavel ?? '',
      endereco: c.endereco ?? '',
      website: c.website ?? '',
      bi: c.bi ?? '',
    });

    this.showDialog = true;
  }

  triggerLogoInput(): void {
    this.fileInputRef?.nativeElement.click();
  }

  onLogoChange(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files?.[0]) return;

    const file = input.files[0];

    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      this.msg.add({
        severity: 'warn',
        summary: 'Formato não suportado',
        detail: 'Apenas imagens PNG ou JPG são aceites.',
        life: 4000,
      });
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      this.logoPreview = e.target?.result as string;
      this.cdr.markForCheck();
    };
    reader.readAsDataURL(file);

    compressImageIfNeeded(file).then((compressed) => {
      this.logoFile = compressed;
      this.cdr.markForCheck();
    });
  }

  searchNif(): void {
    const nif = this.form.get('nif')?.value?.trim();

    if (!nif) {
      this.msg.add({
        severity: 'warn',
        summary: 'NIF em falta',
        detail: 'Introduza um NIF.',
        life: 3000,
      });

      return;
    }

    this.nifSearching = true;

    this.nifSvc
      .lookup(nif)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (c) => {
          this.form.patchValue({
            nome: c.nome,
          });

          this.msg.add({
            severity: 'success',
            summary: 'NIF encontrado',
            detail: c.nome,
            life: 3000,
          });

          this.nifSearching = false;

          this.cdr.markForCheck();
        },

        error: () => {
          this.msg.add({
            severity: 'info',
            summary: 'NIF não encontrado',
            detail: 'Preencha manualmente.',
            life: 4000,
          });

          this.nifSearching = false;

          this.cdr.markForCheck();
        },
      });
  }

 save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.msg.add({
        severity: 'warn',
        summary: 'Formulário Inválido',
        detail: 'Por favor, preencha todos os campos obrigatórios.',
        life: 4000,
      });
      return;
    }

    const formValue = this.form.value;

    // Obtém o valor numérico do enum do sector
    const sectorOption = this.sectors.find((s) => s.value === formValue.sector);
    const setorEnum = sectorOption ? sectorOption.enumValue : 1;

    // Constrói o FormData
    const formData = new FormData();
    formData.append('Nome', formValue.nome ?? '');
    formData.append('Nif', formValue.nif ?? '');
    formData.append('Bi', formValue.bi ?? '');
    formData.append('NumTelefone', formValue.numTelefone ?? '');
    formData.append('NomeResponsavel', formValue.nomeResponsavel ?? '');
    formData.append('WebSite', formValue.website ?? '');
    formData.append('Email', formValue.email ?? '');
    formData.append('Endereco', formValue.endereco ?? '');
    formData.append('SetorAtividadeEmpresaEnum', setorEnum.toString());

    if (this.logoFile) {
      formData.append('Fotografia', this.logoFile, this.logoFile.name);
    } else {
      formData.append('Fotografia', '');
    }

    // UTILIZAR OS MÉTODOS ESPECÍFICOS DE FORMDATA DO CLIENTESERVICE
    const operation = this.editing?.id
      ? this.svc.updateWithFormData(this.editing.id, formData)
      : this.svc.createWithFormData(formData);

    operation.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.msg.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Cliente guardado.',
          life: 3000,
        });

        this.showDialog = false;
        this.load();
        this.estatisticasClientes();
      },

      error: (err) => {
        console.error('Erro ao guardar cliente:', err);

        // Mapeamento e tradução das mensagens de erro recebidas da API
        const errorMessagesMap: Record<string, string> = {
          'Nif': 'O NIF é obrigatório.',
          'The Nif field is required.': 'O NIF é obrigatório.',
          'Nome': 'O nome é obrigatório.',
          'The Nome field is required.': 'O nome é obrigatório.',
          'Email': 'O e-mail é obrigatório.',
          'The Email field is required.': 'O e-mail é obrigatório.',
          'WebSite': 'O website é obrigatório.',
          'The WebSite field is required.': 'O website é obrigatório.',
          'Endereco': 'O endereço é obrigatório.',
          'The Endereco field is required.': 'O endereço é obrigatório.',
          'NumTelefone': 'O número de telefone é obrigatório.',
          'The NumTelefone field is required.': 'O número de telefone é obrigatório.',
          'NomeResponsavel': 'O nome do responsável é obrigatório.',
          'The NomeResponsavel field is required.': 'O nome do responsável é obrigatório.',
        };

        const backendErrors = err?.error?.errors;
        let errorMessageDisplayed = false;

        if (backendErrors && typeof backendErrors === 'object') {
          Object.keys(backendErrors).forEach((field) => {
            const mappedMsg =
              errorMessagesMap[field] ||
              (Array.isArray(backendErrors[field]) ? backendErrors[field][0] : null);

            if (mappedMsg) {
              const customDetail = errorMessagesMap[mappedMsg] || mappedMsg;
              this.msg.add({
                severity: 'error',
                summary: 'Erro de Validação',
                detail: customDetail,
                life: 5000,
              });
              errorMessageDisplayed = true;
            }
          });
        }

        if (!errorMessageDisplayed) {
          const rawMessage = err?.error?.title || err?.error?.message || err?.message || '';
          let customMsg = 'Falha ao guardar.';

          Object.keys(errorMessagesMap).forEach((key) => {
            if (rawMessage.includes(key)) {
              customMsg = errorMessagesMap[key];
            }
          });

          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail: customMsg,
            life: 5000,
          });
        }
      },
    });
  }

  // ─────────────────────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────────────────────

  getInitials(nome: string): string {
    return nome
      .split(' ')
      .filter((n) => n.length > 0)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  getAvatarColor(nome: string): string {
    const colors = [
      '#1e6fff',
      '#8b5cf6',
      '#22c55e',
      '#f59e0b',
      '#ef4444',
      '#06b6d4',
      '#ec4899',
    ];

    let hash = 0;

    for (let i = 0; i < nome.length; i++) {
      hash = nome.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  }

  getSectorLabel(value?: string): string {
    if (!value) {
      return '—';
    }

    return (
      this.sectors.find((s) => s.value === value)?.label ?? value
    );
  }

  getSectorClass(value?: string): string {
    const map: Record<string, string> = {
      BANCA_E_FINANCAS: 'sc-banca',
      PETROLEO_E_GAS: 'sc-petroleo',
      AVIACAO_E_AEROPORTOS: 'sc-aviacao',
      TELECOMUNICACOES: 'sc-telecom',
      SAUDE: 'sc-saude',
      COMERCIO_E_RETALHO: 'sc-comercio',
      HOTELARIA_E_TURISMO: 'sc-hotelaria',
      INDUSTRIA_E_MANUFATURA: 'sc-industria',
      SECTOR_PUBLICO: 'sc-publico',
      CONSTRUCAO: 'sc-construcao',
      OUTRO: 'sc-outro',
    };

    return value ? (map[value] ?? 'sc-outro') : '';
  }
}
